import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEO from "../../components/common/SEO";

export default function HomePage() {
  const siteUrl = "https://la-antigua-guatemala.com";

  return (
    <>
      <SEO
        title="Antigua Guatemala Travel Guide | Things to Do, Hotels, Volcano Hikes"
        description="Plan your trip to Antigua Guatemala with the best things to do, where to stay, volcano hikes (Acatenango & Pacaya), day trips, safety tips, and airport transfers from Guatemala City."
        keywords="Antigua Guatemala travel guide, things to do in Antigua Guatemala, Antigua hotels, Antigua restaurants, Acatenango hike, Pacaya volcano, day trips from Antigua, Guatemala City airport to Antigua, is Antigua Guatemala safe"
        canonical={`${siteUrl}/`}
        ogImage={{
          url: `${siteUrl}/og/home.jpg`,
          width: 1200,
          height: 630,
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": `${siteUrl}/#website`,
              "name": "Antigua Guatemala Travel Guide",
              "url": `${siteUrl}/`,
              "potentialAction": {
                "@type": "SearchAction",
                "target": `${siteUrl}/search?q={search_term_string}`,
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "WebPage",
              "@id": `${siteUrl}/#webpage`,
              "url": `${siteUrl}/`,
              "name": "Antigua Guatemala Travel Guide",
              "isPartOf": { "@id": `${siteUrl}/#website` },
              "description":
                "Complete guide to Antigua Guatemala: activities, hotels, volcano hikes, day trips, safety, transport, and practical tips.",
              "primaryImageOfPage": {
                "@type": "ImageObject",
                "@id": `${siteUrl}/#primaryimage`,
                "url": "https://public.readdy.ai/ai/img_res/333a2b1de1dd2e1f7c0b83eae8ca6511.jpg",
                "width": 1920,
                "height": 1080,
              },
            },
            {
              "@type": "Organization",
              "@id": `${siteUrl}/#org`,
              "name": "Antigua Guatemala Travel Guide",
              "url": `${siteUrl}/`,
              "logo": {
                "@type": "ImageObject",
                "url": `${siteUrl}/logo.png`,
              },
            },
          ],
        }}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[calc(100vh-80px)] lg:min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stunning%20panoramic%20view%20of%20antigua%20guatemala%20colonial%20city%20with%20colorful%20buildings%20cobblestone%20streets%20and%20majestic%20volcano%20de%20agua%20in%20background%20under%20clear%20blue%20sky%20vibrant%20colonial%20architecture%20with%20pastel%20yellow%20orange%20and%20red%20facades%20simple%20clean%20background&width=1920&height=1080&seq=hero-antigua-main&orientation=landscape"
            alt="Antigua Guatemala colonial city with volcano views"
            className="w-full h-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white py-12 lg:py-20">
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 drop-shadow-lg leading-tight">
            Antigua Guatemala
            <br />
            Travel Guide
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl mb-8 max-w-3xl mx-auto drop-shadow-md font-light">
            Everything US travelers need: top things to do, where to stay, volcano hikes, day trips, safety tips,
            and airport transfers.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/plan-my-antigua-trip"
              className="whitespace-nowrap bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
            >
              Plan My Trip
            </Link>
            <Link
              to="/things-to-do-in-antigua-guatemala"
              className="whitespace-nowrap bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 border-2 border-white/30 cursor-pointer"
            >
              Explore Things to Do
            </Link>
          </div>

          <p className="mt-6 text-sm text-white/80">
            Popular for first-timers • Walkable historic center • Volcano adventures nearby
          </p>
        </div>
      </section>

      {/* Quick Answers */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Quick Answers for Planning Antigua Guatemala
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 flex-shrink-0">
                  <i className="ri-calendar-line text-2xl" />
                </div>
                <div className="ml-4">
                  <h3 className="font-bold text-gray-900 mb-2">How Many Days in Antigua?</h3>
                  <p className="text-gray-700 text-sm">
                    Most visitors stay <strong>2–4 days</strong> for city highlights and a volcano hike. Start with the{" "}
                    <Link to="/antigua-guatemala-3-day-itinerary" className="text-teal-500 hover:text-teal-600 font-semibold">
                      3-day itinerary
                    </Link>{" "}
                    or go deeper with the{" "}
                    <Link to="/antigua-guatemala-2-week-itinerary" className="text-teal-500 hover:text-teal-600 font-semibold">
                      longer plan
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 flex-shrink-0">
                  <i className="ri-plane-line text-2xl" />
                </div>
                <div className="ml-4">
                  <h3 className="font-bold text-gray-900 mb-2">How to Get to Antigua</h3>
                  <p className="text-gray-700 text-sm">
                    Fly into Guatemala City (GUA), then take a shuttle or private transfer. See the full guide{" "}
                    <Link to="/guatemala-city-airport-to-antigua" className="text-teal-500 hover:text-teal-600 font-semibold">
                      here
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl" />
                </div>
                <div className="ml-4">
                  <h3 className="font-bold text-gray-900 mb-2">Is Antigua Safe?</h3>
                  <p className="text-gray-700 text-sm">
                    Antigua is generally safe in tourist areas with visible security. Read practical do’s and don’ts in the{" "}
                    <Link to="/is-antigua-guatemala-safe" className="text-teal-500 hover:text-teal-600 font-semibold">
                      safety guide
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 flex-shrink-0">
                  <i className="ri-hotel-line text-2xl" />
                </div>
                <div className="ml-4">
                  <h3 className="font-bold text-gray-900 mb-2">Where to Stay</h3>
                  <p className="text-gray-700 text-sm">
                    Stay near Parque Central for the most walkable experience. Browse recommendations in{" "}
                    <Link to="/best-hotels-in-antigua-guatemala" className="text-teal-500 hover:text-teal-600 font-semibold">
                      the hotel guide
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 flex-shrink-0">
                  <i className="ri-mountain-line text-2xl" />
                </div>
                <div className="ml-4">
                  <h3 className="font-bold text-gray-900 mb-2">Best Volcano Hikes</h3>
                  <p className="text-gray-700 text-sm">
                    The iconic choice is{" "}
                    <Link to="/acatenango-hike-from-antigua" className="text-teal-500 hover:text-teal-600 font-semibold">
                      Acatenango
                    </Link>{" "}
                    (overnight). For a shorter option, try{" "}
                    <Link to="/pacaya-volcano-day-trip-from-antigua" className="text-teal-500 hover:text-teal-600 font-semibold">
                      Pacaya
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-start">
                <div className="w-12 h-12 flex items-center justify-center text-teal-500 flex-shrink-0">
                  <i className="ri-map-2-line text-2xl" />
                </div>
                <div className="ml-4">
                  <h3 className="font-bold text-gray-900 mb-2">Best Day Trips</h3>
                  <p className="text-gray-700 text-sm">
                    Antigua is a perfect base for Lake Atitlán, markets, and viewpoints. Start with{" "}
                    <Link to="/day-trips-from-antigua-guatemala" className="text-teal-500 hover:text-teal-600 font-semibold">
                      day trips from Antigua
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-lg text-gray-700 leading-relaxed">
            Antigua Guatemala is a UNESCO World Heritage colonial city known for colorful facades, cobblestone streets,
            dramatic volcano views, and easy access to outdoor adventures. Use this hub to plan your visit with confidence:
            must-see attractions, the best neighborhoods to stay, volcano hikes like Acatenango and Pacaya, day trips,
            transportation from Guatemala City airport, and safety tips tailored for US travelers.
          </p>
        </div>
      </section>

      {/* Start Here */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Start Here</h2>
            <p className="text-xl text-gray-600">The essential guides for planning Antigua Guatemala</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Link
              to="/antigua-guatemala-travel-guide"
              className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer border-2 border-teal-500"
            >
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                <i className="ri-book-open-line" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Complete Antigua Guide</h3>
              <p className="text-gray-600">
                One place for all the basics: neighborhoods, local etiquette, money tips, safety, and what to book in advance.
              </p>
            </Link>

            <Link to="/antigua-guatemala-3-day-itinerary" className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                <i className="ri-calendar-check-line" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Best 3-Day Itinerary</h3>
              <p className="text-gray-600">
                The fastest way to see the highlights: city walk + ruins + Acatenango (or Pacaya) + an easy culture day trip.
              </p>
            </Link>

            <Link to="/guatemala-city-airport-to-antigua" className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                <i className="ri-plane-line" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Airport Transfer Guide</h3>
              <p className="text-gray-600">
                Shuttle vs private car, what to expect on arrival, and how to book safely from Guatemala City (GUA).
              </p>
            </Link>

            <Link to="/best-hotels-in-antigua-guatemala" className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                <i className="ri-hotel-line" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Where to Stay</h3>
              <p className="text-gray-600">
                Best neighborhoods and hand-picked hotels for every budget (walkable, safe, great atmosphere).
              </p>
            </Link>

            <Link to="/day-trips-from-antigua-guatemala" className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                <i className="ri-map-2-line" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Top Day Trips</h3>
              <p className="text-gray-600">
                Lake Atitlán, markets, viewpoints, volcanoes, and easy escapes that fit perfectly into an Antigua-based trip.
              </p>
            </Link>

            <Link to="/plan-my-antigua-trip" className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                <i className="ri-compass-3-line" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Trip Planning Tool</h3>
              <p className="text-gray-600">
                Get tailored recommendations for tours, shuttles, and stays based on your travel style and time available.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Top Things to Do */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Top Things to Do in Antigua Guatemala</h2>
            <p className="text-xl text-gray-600">The must-see attractions and experiences</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/things-to-do-in-antigua-guatemala" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl mb-4">
                <i className="ri-building-4-line" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Santa Catalina Arch</h3>
              <p className="text-sm text-gray-600">The iconic photo spot with a volcano backdrop.</p>
            </Link>

            <Link to="/things-to-do-in-antigua-guatemala" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl mb-4">
                <i className="ri-landscape-line" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cerro de la Cruz</h3>
              <p className="text-sm text-gray-600">Easy viewpoint walk with sweeping city views.</p>
            </Link>

            <Link to="/things-to-do-in-antigua-guatemala" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl mb-4">
                <i className="ri-ancient-gate-line" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Ruins & Churches</h3>
              <p className="text-sm text-gray-600">Historic sites that define Antigua’s charm.</p>
            </Link>

            <Link to="/things-to-do-in-antigua-guatemala" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl mb-4">
                <i className="ri-cup-line" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Coffee Farm Tour</h3>
              <p className="text-sm text-gray-600">Taste world-class beans and learn the process.</p>
            </Link>

            <Link to="/things-to-do-in-antigua-guatemala" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl mb-4">
                <i className="ri-shopping-bag-line" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Markets & Crafts</h3>
              <p className="text-sm text-gray-600">Textiles, souvenirs, and local artisan finds.</p>
            </Link>

            <Link to="/things-to-do-in-antigua-guatemala" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl mb-4">
                <i className="ri-goblet-line" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Rooftop Sunsets</h3>
              <p className="text-sm text-gray-600">Golden hour views over the volcanoes.</p>
            </Link>

            <Link to="/acatenango-hike-from-antigua" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl mb-4">
                <i className="ri-tent-line" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Acatenango Hike</h3>
              <p className="text-sm text-gray-600">The famous overnight volcano trek.</p>
            </Link>

            <Link to="/pacaya-volcano-day-trip-from-antigua" className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition cursor-pointer">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl mb-4">
                <i className="ri-fire-line" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Pacaya Volcano</h3>
              <p className="text-sm text-gray-600">A shorter adventure with lava landscapes.</p>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/things-to-do-in-antigua-guatemala"
              className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer"
            >
              See All Things to Do
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Itineraries */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Start Planning Your Trip</h2>
            <p className="text-xl text-gray-600">Pick the itinerary that matches your time and travel style</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/antigua-guatemala-3-day-itinerary" className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition cursor-pointer border-2 border-teal-500">
              <div className="relative h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=beautiful%20view%20of%20antigua%20guatemala%20historic%20center%20with%20santa%20catalina%20arch%20and%20volcano%20in%20background%20tourists%20walking%20on%20cobblestone%20streets%20colorful%20colonial%20buildings%20perfect%20weather%20vibrant%20atmosphere%20simple%20clean%20background&width=800&height=600&seq=itinerary-3day-card&orientation=landscape"
                  alt="Antigua Guatemala itinerary for first-time visitors"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute top-4 right-4 bg-teal-500 text-white px-4 py-2 rounded-lg font-bold text-sm">
                  3 Days
                </div>
                <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full font-bold text-xs">
                  BEST START
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-500 transition">First-Time Visitor</h3>
                <p className="text-gray-600 mb-4">
                  The ideal intro: city highlights + a volcano experience + a cultural day trip.
                </p>
                <div className="flex items-center text-teal-500 font-semibold">
                  View Itinerary <i className="ri-arrow-right-line ml-2" />
                </div>
              </div>
            </Link>

            <Link to="/antigua-guatemala-2-week-itinerary" className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition cursor-pointer">
              <div className="relative h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=scenic%20landscape%20of%20guatemala%20with%20lake%20atitlan%20surrounded%20by%20volcanoes%20traditional%20mayan%20villages%20colorful%20boats%20on%20water%20lush%20green%20mountains%20peaceful%20atmosphere%20simple%20clean%20background&width=800&height=600&seq=itinerary-2week-card&orientation=landscape"
                  alt="Two-week Guatemala itinerary including Antigua Guatemala"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute top-4 right-4 bg-teal-500 text-white px-4 py-2 rounded-lg font-bold text-sm">
                  Longer Trip
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-500 transition">Deeper Guatemala</h3>
                <p className="text-gray-600 mb-4">
                  A bigger route with Antigua as the anchor plus other iconic destinations across the country.
                </p>
                <div className="flex items-center text-teal-500 font-semibold">
                  View Itinerary <i className="ri-arrow-right-line ml-2" />
                </div>
              </div>
            </Link>

            <Link to="/day-trips-from-antigua-guatemala" className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition cursor-pointer">
              <div className="relative h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=beautiful%20guatemalan%20landscape%20with%20active%20volcano%20smoking%20in%20distance%20winding%20mountain%20roads%20lush%20green%20valleys%20traditional%20villages%20colorful%20scenery%20adventure%20travel%20destination%20simple%20clean%20background&width=800&height=600&seq=daytrips-card&orientation=landscape"
                  alt="Day trips from Antigua Guatemala"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-500 transition">Antigua Basecamp</h3>
                <p className="text-gray-600 mb-4">
                  Stay in Antigua and explore nearby volcanoes, lakes, markets, and viewpoints without moving hotels often.
                </p>
                <div className="flex items-center text-teal-500 font-semibold">
                  See Day Trips <i className="ri-arrow-right-line ml-2" />
                </div>
              </div>
            </Link>
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            Tip: If you only have a short trip, prioritize Antigua + one volcano (Acatenango or Pacaya).
          </p>
        </div>
      </section>

      {/* Adventures */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Adventures from Antigua</h2>
            <p className="text-xl text-gray-600">Volcano hikes and day trips worth planning around</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/acatenango-hike-from-antigua" className="group bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer">
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=dramatic%20view%20of%20acatenango%20volcano%20summit%20with%20camping%20tents%20at%20sunset%20erupting%20fuego%20volcano%20in%20background%20with%20lava%20explosions%20hikers%20silhouettes%20orange%20and%20purple%20sky%20adventure%20atmosphere%20simple%20clean%20background&width=600&height=400&seq=acatenango-adventure&orientation=landscape"
                  alt="Acatenango volcano hike from Antigua Guatemala"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Acatenango Volcano</h3>
                <p className="text-sm text-gray-600">Overnight hike with views of erupting Fuego.</p>
              </div>
            </Link>

            <Link to="/pacaya-volcano-day-trip-from-antigua" className="group bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer">
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=pacaya%20volcano%20landscape%20with%20black%20volcanic%20rocks%20and%20lava%20fields%20hikers%20walking%20on%20volcanic%20terrain%20steam%20rising%20from%20ground%20dramatic%20volcanic%20scenery%20adventure%20hiking%20simple%20clean%20background&width=600&height=400&seq=pacaya-adventure&orientation=landscape"
                  alt="Pacaya volcano day trip from Antigua Guatemala"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Pacaya Volcano</h3>
                <p className="text-sm text-gray-600">Shorter hike with lava landscapes and big views.</p>
              </div>
            </Link>

            <Link to="/antigua-to-chichicastenango" className="group bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer">
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=vibrant%20chichicastenango%20market%20in%20guatemala%20with%20colorful%20traditional%20textiles%20handicrafts%20indigenous%20vendors%20in%20traditional%20clothing%20bustling%20market%20atmosphere%20bright%20colors%20cultural%20scene%20simple%20clean%20background&width=600&height=400&seq=chichi-market&orientation=landscape"
                  alt="Chichicastenango market day trip from Antigua"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Chichicastenango</h3>
                <p className="text-sm text-gray-600">One of the most famous indigenous markets in Guatemala.</p>
              </div>
            </Link>

            <Link to="/hobbitenango-antigua-guatemala" className="group bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer">
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=hobbitenango%20viewpoint%20park%20near%20antigua%20guatemala%20with%20giant%20hand%20sculpture%20overlooking%20mountains%20and%20valleys%20panoramic%20mountain%20scenery%20whimsical%20installations%20tourists%20enjoying%20views%20simple%20clean%20background&width=600&height=400&seq=hobbitenango-view&orientation=landscape"
                  alt="Hobbitenango viewpoint near Antigua Guatemala"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Hobbitenango</h3>
                <p className="text-sm text-gray-600">Fun viewpoint park with panoramic mountain scenery.</p>
              </div>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/day-trips-from-antigua-guatemala"
              className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer"
            >
              See All Day Trips
            </Link>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Where to Stay in Antigua</h2>
            <p className="text-xl text-gray-600">Neighborhoods and hotels for every budget</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/best-hotels-in-antigua-guatemala" className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer">
              <div className="relative h-56">
                <img
                  src="https://readdy.ai/api/search-image?query=luxury%20boutique%20hotel%20in%20antigua%20guatemala%20with%20beautiful%20colonial%20courtyard%20fountain%20tropical%20plants%20elegant%20architecture%20colorful%20tiles%20rooftop%20terrace%20with%20volcano%20views%20upscale%20atmosphere%20simple%20clean%20background&width=800&height=600&seq=hotels-luxury&orientation=landscape"
                  alt="Luxury boutique hotels in Antigua Guatemala"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-6">
                <div className="text-sm font-semibold text-teal-500 mb-2">LUXURY</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Boutique Hotels</h3>
                <p className="text-gray-600">Colonial courtyards, rooftop terraces, and elevated service.</p>
              </div>
            </Link>

            <Link to="/best-hotels-in-antigua-guatemala" className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer">
              <div className="relative h-56">
                <img
                  src="https://readdy.ai/api/search-image?query=comfortable%20mid%20range%20hotel%20in%20antigua%20guatemala%20with%20cozy%20rooms%20colonial%20style%20decor%20clean%20modern%20amenities%20welcoming%20atmosphere%20good%20value%20accommodation%20simple%20clean%20background&width=800&height=600&seq=hotels-midrange&orientation=landscape"
                  alt="Best value hotels in Antigua Guatemala"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-6">
                <div className="text-sm font-semibold text-teal-500 mb-2">MID-RANGE</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Best Value</h3>
                <p className="text-gray-600">Comfort, location, and amenities without overpaying.</p>
              </div>
            </Link>

            <Link to="/best-hotels-in-antigua-guatemala" className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer">
              <div className="relative h-56">
                <img
                  src="https://readdy.ai/api/search-image?query=vibrant%20hostel%20in%20antigua%20guatemala%20with%20social%20common%20area%20colorful%20decor%20backpackers%20hanging%20out%20friendly%20atmosphere%20budget%20accommodation%20travel%20community%20simple%20clean%20background&width=800&height=600&seq=hotels-budget&orientation=landscape"
                  alt="Best hostels in Antigua Guatemala"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-6">
                <div className="text-sm font-semibold text-teal-500 mb-2">BUDGET</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Top Hostels</h3>
                <p className="text-gray-600">Social stays, tours, and great value for solo travelers.</p>
              </div>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/best-hotels-in-antigua-guatemala"
              className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer"
            >
              Complete Hotel Guide
            </Link>
          </div>
        </div>
      </section>

      {/* Safety + Transport */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Is Antigua Guatemala Safe?</h2>
              <p className="text-lg text-gray-700 mb-6">
                Antigua is one of the most visited destinations in Guatemala, and many US travelers have trouble-free trips,
                especially in the historic center. Like any city, standard precautions make a big difference.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl" />
                  </div>
                  <span className="ml-3 text-gray-700">Stay in well-lit, busy areas at night</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl" />
                  </div>
                  <span className="ml-3 text-gray-700">Use trusted taxis, tuk-tuks, or arranged rides</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl" />
                  </div>
                  <span className="ml-3 text-gray-700">Book airport transfers in advance</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-teal-500 mt-1">
                    <i className="ri-check-line text-xl" />
                  </div>
                  <span className="ml-3 text-gray-700">Keep valuables secure and low-profile</span>
                </li>
              </ul>

              <Link
                to="/is-antigua-guatemala-safe"
                className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer"
              >
                Read the Safety Guide
              </Link>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Getting to Antigua from the Airport</h2>
              <p className="text-lg text-gray-700 mb-6">
                Most US travelers arrive at Guatemala City (GUA) and then transfer to Antigua. The simplest options are a
                shared shuttle or a private car.
              </p>

              <div className="space-y-4 mb-8">
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">Shared Shuttle</h3>
                  <p className="text-gray-700 text-sm">Budget-friendly, common for solo travelers. May have fixed schedules.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">Private Transfer</h3>
                  <p className="text-gray-700 text-sm">Fast and convenient, ideal for families or late arrivals.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">Rental Car</h3>
                  <p className="text-gray-700 text-sm">Possible, but traffic and parking can be stressful for first-time visitors.</p>
                </div>
              </div>

              <Link
                to="/guatemala-city-airport-to-antigua"
                className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer"
              >
                Airport Transfer Guide
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Antigua Guatemala FAQ</h2>
            <p className="text-xl text-gray-600">Quick answers to common questions</p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "How many days should I spend in Antigua Guatemala?",
                a: "Most travelers plan 2–4 days for city highlights plus a volcano hike or day trip. If you’re adding Lake Atitlán or multiple excursions, staying longer makes Antigua an easy base.",
              },
              {
                q: "Is Antigua Guatemala safe for US tourists?",
                a: "Antigua is generally considered one of the safest tourist hubs in Guatemala, especially around the historic center. Use common-sense precautions and arrange airport transfers in advance.",
              },
              {
                q: "What is the best volcano hike near Antigua?",
                a: "Acatenango is the most famous (overnight) for views of erupting Fuego. Pacaya is a shorter, easier day trip option with volcanic landscapes.",
              },
              {
                q: "How do I get from Guatemala City airport (GUA) to Antigua?",
                a: "The most common options are shared shuttles and private transfers. Private transfers are best for late arrivals and families, while shuttles are popular for solo travelers.",
              },
              {
                q: "Where is the best area to stay in Antigua?",
                a: "Staying near Parque Central is the most walkable for restaurants, tours, and attractions. Many visitors also like quieter streets a few blocks away for better sleep.",
              },
              {
                q: "What are the top things to do in Antigua Guatemala?",
                a: "Most visitors prioritize the Santa Catalina Arch, Cerro de la Cruz viewpoint, colonial ruins, coffee tours, rooftop sunsets, and a volcano hike.",
              },
              {
                q: "Can I do Lake Atitlán as a day trip from Antigua?",
                a: "It’s possible but long. Many travelers prefer staying at Lake Atitlán for at least one night if time allows, then returning to Antigua.",
              },
              {
                q: "Do I need to book Acatenango in advance?",
                a: "Booking ahead is recommended, especially in busy periods. Choose a reputable operator and confirm inclusions like gear, meals, and transport.",
              },
            ].map((item) => (
              <div key={item.q} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-700">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Plan Your Antigua Guatemala Trip</h2>
          <p className="text-xl text-gray-300 mb-8">
            Answer a few questions and get tailored recommendations for tours, shuttles, and where to stay.
          </p>
          <Link
            to="/plan-my-antigua-trip"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer"
          >
            Get Personalized Recommendations
          </Link>
          <p className="mt-6 text-sm text-white/70">
            Prefer browsing? Start with{" "}
            <Link to="/things-to-do-in-antigua-guatemala" className="underline hover:text-white">
              things to do
            </Link>{" "}
            or{" "}
            <Link to="/best-hotels-in-antigua-guatemala" className="underline hover:text-white">
              where to stay
            </Link>
            .
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
}